/* eslint-disable jira/filename-case */
import type {
	ApplicationEdition,
	ApplicationEditions,
} from '@jira/platform__application-editions/src/edition.tsx';
import type {
	Environment,
	TenantContext,
} from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import { isStableUIEnabledForTests } from '@jira/platform__is-stable-ui-enabled-for-tests/src/isStableUIEnabledForTests.tsx';
import { operationsConverter } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/selected-path/route-converters/operations/index.tsx';
import { JiraWorkspaceAri } from '@atlassian/ari/jira';
import type { PostOfficeEnvironmentConfig } from '@atlassian/post-office-context/post-office/types';
import type { PostOfficeContextValue } from '@atlassian/post-office-context/types';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';

const JIRA_PRODUCT = 'jira';

const DEFAULT_POST_OFFICE_CONTEXT = {
	product: JIRA_PRODUCT,
	locale: 'en',
};

const getWorkspaceARI = ({ cloudId, activationId }: { cloudId: string; activationId: string }) => {
	return JiraWorkspaceAri.create({
		activationId,
		siteId: cloudId,
	}).toString();
};

export type JiraTenantDataFields =
	| 'activationId'
	| 'cloudId'
	| 'isSiteAdmin'
	| 'locale'
	| 'isAdmin';

type JiraPostOfficeContextValue = PostOfficeContextValue & { isAdmin: boolean };

export const tenantToPostOfficeContext = (
	tenantData: Pick<TenantContext, JiraTenantDataFields>,
): JiraPostOfficeContextValue => {
	const { activationId, cloudId, isSiteAdmin, locale, isAdmin } = tenantData;

	const workspaceAri = getWorkspaceARI({ activationId, cloudId });

	return {
		...DEFAULT_POST_OFFICE_CONTEXT,
		locale,
		isSiteAdmin,
		tenantId: cloudId,
		workspaceAri,
		workspaceId: activationId,
		isAdmin,
		orgId: undefined,
		subproduct: undefined,
		productEdition: undefined,
		disableAllPlacements:
			isStableUIEnabledForTests() && fg('jpx-870-stable-ui-for-criterion') ? true : undefined,
	};
};

export const subproductToEdition = (
	subproduct: string | undefined,
	editions: ApplicationEditions,
): ApplicationEdition | undefined => {
	switch (subproduct) {
		case 'core':
			return editions.core;
		case 'software':
			return editions.software;
		case 'customerService':
			return editions.customerService;
		case 'service_desk':
		case 'serviceDesk':
			return editions.serviceDesk;
		default:
			undefined;
	}
};

export const transformJiraEnvToPostOfficeEnvironment = (
	environment: Environment | null,
): PostOfficeEnvironmentConfig['env'] => {
	switch (environment) {
		case 'dev':
		case 'staging':
			return 'staging';
		case 'prod':
			return 'production';
		case 'local':
			return 'local';
		default:
			return 'local';
	}
};

/**
 * Derives the subproduct value for Post Office context.
 *
 * Most routes provide subproduct via project data, but /jira/ops routes
 * don't pass subproduct through their route data. For the jsmgrowth466
 * experiment, we fall back to 'service_desk' when on an ops route.
 */
export const useSubproduct = (projectType?: string): string | undefined => {
	const isOpsRoute = useIsActiveRoute(operationsConverter.routes);
	const [jsmgrowth466Star] = UNSAFE_noExposureExp('jsmgrowth466_star');

	return (
		projectType ??
		(isOpsRoute && jsmgrowth466Star.get('cohort', 'not-enrolled') === 'variation'
			? 'service_desk'
			: undefined)
	);
};
